import {
  DragDropContext,
  type DraggableLocation,
  Droppable,
} from "@hello-pangea/dnd";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { type ISection } from "src/interfaces";
import {
  DynamicSectionWrapper,
  StyledTable,
  StyledTableWrapper,
} from "src/styled";
import tableStyles from "src/styled/sharedTable.module.scss";
import { v4 as uuidv4 } from "uuid";

import { useTheme } from "../../../hooks";
import { TimeoutProvider } from "../../../hooks/useTimeoutCall";
import { useDispatch, useSelector } from "../../../redux/hooks";
import { setEditing } from "../../../redux/slices/entrySlice";
import {
  reorderRows,
  toggleSectionCollapse,
} from "../../../redux/slices/generatorSlice";
import { checkIsCaptain } from "../../../redux/utils/checkIsCaptain";
import useCheckTimeOrder from "../../hooks/useCheckTimeOrder";
import { useStickyHorizontalScroll } from "../../hooks/useStickyHorizontalScroll";
import { useTriggerAutoSave } from "../../hooks/useTriggerAutoSave";
import LogbookOverlay from "../../LogbookOverlay/LogbookOverlay";
import Row from "../Row/Row";
import AddDynamicRow from "./AddDynamicRow/AddDynamicRow";
import ControlDynamicSection from "./ControlDynamicSection/ControlDynamicSection";
import { generateHeaders } from "./PortraitSectionHeaders/generateHeaders";
import PortraitSectionTitle from "./PortraitSectionTitle";

interface Props extends ISection {
  sectionIndex: number;
  className: string;
  isOverlay?: boolean;
  pageRef?: React.RefObject<HTMLDivElement>;
  setHeaderHeight?: (value: number) => void;
  isShared?: boolean;
  sharedIndex?: number;
}

const PortraitSection = forwardRef<HTMLDivElement | null, Props>(
  (
    {
      title,
      subtitle,
      headers,
      rows,
      dynamicRows = false,
      addRowsToTop,
      showDateTimePerRow,
      sectionIndex,
      sharedIndex,
      headerSize,
      cloneRows,
      copyPrevVal,
      fixedWidth,
      fixedLayout,
      freezePanes,
      grayOldRows,
      noBorders,
      draggable,
      center,
      maxWidth,
      inlineSaving,
      authRequiredForDeletion,
      className,
      captainComments,
      isOverlay,
      pageRef,
      showVitals,
      showSubRowHeaders,
      strikethroughRows,
      NMEA_button,
      isShared,
      dynamicSection,
      cloneSection,
      collapseable,
      collapsed,
      buttonOrientation,
      useMarginWithRows,
    }: Props,
    ref,
  ) => {
    const theme = useTheme() as any;
    const dispatch = useDispatch();
    const triggerAutoSave = useTriggerAutoSave();
    const { rolename } = useSelector(state => state.permsSlice);
    const {
      addRowsToTop: globalAddRowsToTop,
      showDateTimePerRow: globalShowDateTimePerRow,
    } = useSelector(state => state.generatorSlice.lbentry);
    const localRef = useRef<HTMLDivElement | null>(null);

    const sectionRef = (ref as React.RefObject<HTMLDivElement>) || localRef;
    const headerRef = useRef<HTMLTableSectionElement | null>(null);

    addRowsToTop = addRowsToTop ?? globalAddRowsToTop ?? false;
    showDateTimePerRow =
      showDateTimePerRow ?? globalShowDateTimePerRow ?? false;

    // Apply sticky scroll to all dynamic sections
    const shouldUseStickyScroll = useMemo(
      () =>
        Boolean(dynamicRows || cloneRows) &&
        !isOverlay &&
        (!collapseable || !collapsed),
      [dynamicRows, cloneRows, isOverlay, collapseable, collapsed],
    );

    const { mainScrollRef, stickyScrollRef, contentRef, updateContentWidth } =
      useStickyHorizontalScroll(shouldUseStickyScroll, true);

    // const { formState } = useFormContext();

    // useEffect(() => {
    //   Object.keys(formState.touchedFields).map(key => {
    //     // get ele
    //   });
    // }, [formState.touchedFields]);

    useCheckTimeOrder({ sectionIndex, dynamicRows });

    // Update sticky scrollbar when table content changes
    useEffect(() => {
      if (shouldUseStickyScroll) {
        updateContentWidth();
      }
    }, [rows, shouldUseStickyScroll, updateContentWidth]);

    const symbolMapRef = useRef(new Map<string, string>());

    const getOrCreateSymbol = useCallback((row: any, index: number) => {
      const rowKey = row.symbol || `row-${index}`;
      if (!symbolMapRef.current.has(rowKey)) {
        symbolMapRef.current.set(rowKey, uuidv4());
      }
      return symbolMapRef.current.get(rowKey)!;
    }, []);

    const processedRows = useMemo(() => {
      // Annotate rows with original index
      const annotatedRows = rows
        ? rows.map((r, i) => ({ ...r, originalIndex: i }))
        : [];

      if (!rows) return [];
      if (dynamicRows && addRowsToTop && showDateTimePerRow) {
        const splitIndex = annotatedRows.findIndex((r: any) => !r.date);

        if (splitIndex === -1) {
          return annotatedRows;
        }

        const rowsWithDate = annotatedRows.slice(0, splitIndex);
        const rowsWithoutDate = annotatedRows.slice(splitIndex).reverse();

        return [...rowsWithDate, ...rowsWithoutDate];
      }
      return annotatedRows;
    }, [rows, dynamicRows, addRowsToTop, showDateTimePerRow]);

    const mappedState = useMemo(
      () =>
        processedRows
          ? processedRows.map((row, idx) => ({
              ...row,
              symbol: getOrCreateSymbol(row, idx),
            }))
          : [],
      [processedRows, getOrCreateSymbol],
    );
    const memoizedRows = useMemo(() => processedRows, [processedRows]);

    const showAddCommentButton = useMemo(
      () => captainComments && checkIsCaptain(rolename),
      [captainComments, rolename],
    );

    const overlayControlledClassName = useMemo(
      () => (isOverlay ? "overlay" : `${className}_scroll`),
      [isOverlay, className],
    );
    const droppableId = useMemo(
      () => `dnd-list-${sectionIndex}`,
      [sectionIndex],
    );

    const handleDragEnd = ({
      destination,
      source,
    }: {
      destination: DraggableLocation | null;
      source: DraggableLocation;
    }) => {
      if (!destination) return;
      if (destination.index === source.index) return;

      const fromIndex =
        processedRows[source.index]?.originalIndex ?? source.index;
      const toIndex =
        processedRows[destination.index]?.originalIndex ?? destination.index;

      if (fromIndex === toIndex) return;

      dispatch(
        reorderRows({
          sectionIndex,
          from: fromIndex,
          to: toIndex,
        }),
      );

      // Regenerate logColumns off the drop frame to avoid UI stutter.
      requestAnimationFrame(() => {
        const run = () => {
          const host = mainScrollRef.current ?? sectionRef.current;
          const elements = host
            ? host.querySelectorAll(".logColumns")
            : document.querySelectorAll(".logColumns");

          elements.forEach(el => {
            const input = el?.querySelector("input");
            input?.dispatchEvent(new Event("change", { bubbles: true }));
          });
        };

        if (typeof window.requestIdleCallback === "function") {
          window.requestIdleCallback(run, { timeout: 200 });
        } else {
          setTimeout(run, 0);
        }
      });

      setTimeout(() => {
        triggerAutoSave();
      }, 0);
    };

    const handleHeaderClick = useCallback(() => {
      if (collapseable && !title?.trim()) {
        dispatch(toggleSectionCollapse({ sectionIndex }));
        dispatch(setEditing(true));
      }
    }, [collapseable, title, dispatch, sectionIndex]);

    const generatedHeaders = useMemo(
      () =>
        generateHeaders({
          headers,
          theme,
          isDynamic: dynamicRows && !showSubRowHeaders,
          headerSize,
          draggable,
          cloneRows: cloneRows && !showSubRowHeaders,
          showAddCommentButton,
          headerRef,
          sectionIndex,
          NMEAButton: NMEA_button,
          collapseable,
          collapsed,
          hasTitle: Boolean(title?.trim()),
          onHeaderClick: handleHeaderClick,
        }),
      [
        showSubRowHeaders,
        headers,
        dynamicRows,
        theme,
        headerSize,
        draggable,
        cloneRows,
        showAddCommentButton,
        headerRef,
        strikethroughRows,
        NMEA_button,
        collapseable,
        collapsed,
        title,
        handleHeaderClick,
      ],
    );

    return (
      <TimeoutProvider>
        <div style={!isOverlay ? { position: "relative" } : {}}>
          {freezePanes && !isOverlay && !showSubRowHeaders && (
            <LogbookOverlay sectionIndex={sectionIndex} />
          )}
          {(dynamicRows || cloneRows) &&
            !isOverlay &&
            (!collapseable || !collapsed) &&
            addRowsToTop && (
              <AddDynamicRow
                sectionsClassName={className}
                inlineSaving={inlineSaving}
                sectionIndex={sectionIndex}
                addRows={dynamicRows}
                cloneRows={cloneRows}
                copyPrevVal={copyPrevVal}
                addToTop={true}
                containerStyle={{
                  marginBottom: "15px",
                }}
              />
            )}
          {shouldUseStickyScroll ? (
            <DynamicSectionWrapper>
              {draggable ? (
                <div
                  style={{
                    width: "100%",
                    paddingBottom: "2px",
                  }}
                  ref={mainScrollRef}
                  className={`${tableStyles.mainContent} ${overlayControlledClassName}`}>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <StyledTable
                      layout={fixedLayout ? "fixed" : undefined}
                      style={{
                        marginTop: "0px",
                        borderWidth: noBorders ? "0px" : "1px",
                      }}
                      className={className}>
                      <PortraitSectionTitle
                        sectionIndex={sectionIndex}
                        title={title}
                        center={center}
                        subtitle={subtitle}
                        showVitals={showVitals}
                        collapseable={collapseable}
                        collapsed={collapsed}
                      />
                      {(!collapseable || !collapsed || !title?.trim()) &&
                        generatedHeaders}
                      {(!collapseable || !collapsed) && (
                        <Droppable
                          droppableId={droppableId}
                          direction="vertical">
                          {provided => (
                            <tbody
                              {...provided.droppableProps}
                              ref={provided.innerRef}>
                              <Row
                                isOverlay={isOverlay}
                                showAddCommentButton={showAddCommentButton}
                                authRequiredForDeletion={
                                  authRequiredForDeletion
                                }
                                rows={mappedState}
                                headers={headers}
                                showSubRowHeaders={showSubRowHeaders}
                                isDynamic={Boolean(dynamicRows || cloneRows)}
                                sectionIndex={sectionIndex}
                                strikethroughRows={strikethroughRows}
                                NMEAButton={NMEA_button}
                                draggable={true}
                                buttonOrientation={buttonOrientation}
                                useMarginWithRows={useMarginWithRows}
                                isShared={isShared}
                                sharedIndex={sharedIndex}
                              />
                              {provided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      )}
                    </StyledTable>
                  </DragDropContext>
                </div>
              ) : (
                <StyledTableWrapper
                  ref={mainScrollRef}
                  className={`${tableStyles.mainContent} ${overlayControlledClassName}`}
                  isDynamic={true}>
                  <StyledTable
                    w={fixedWidth ?? undefined}
                    layout={fixedLayout ? "fixed" : undefined}
                    className={className}
                    style={{
                      marginTop: "0px",
                      borderWidth: noBorders ? "0px" : "1px",
                    }}>
                    <PortraitSectionTitle
                      sectionIndex={sectionIndex}
                      title={title}
                      center={center}
                      subtitle={subtitle}
                      showVitals={showVitals}
                      collapseable={collapseable}
                      collapsed={collapsed}
                    />

                    {(!collapseable || !collapsed || !title?.trim()) &&
                      generatedHeaders}
                    {(!collapseable || !collapsed) && (
                      <Row
                        isOverlay={isOverlay}
                        showAddCommentButton={showAddCommentButton}
                        headers={headers}
                        showSubRowHeaders={showSubRowHeaders}
                        authRequiredForDeletion={authRequiredForDeletion}
                        rows={memoizedRows}
                        isDynamic={Boolean(dynamicRows || cloneRows)}
                        sectionIndex={sectionIndex}
                        strikethroughRows={strikethroughRows}
                        buttonOrientation={buttonOrientation}
                        useMarginWithRows={useMarginWithRows}
                        isShared={isShared}
                        sharedIndex={sharedIndex}
                      />
                    )}
                  </StyledTable>
                </StyledTableWrapper>
              )}

              {/* Sticky horizontal scrollbar */}
              {(!collapseable || !collapsed) && (
                <div className={tableStyles.stickyScrollbar} ref={stickyScrollRef}>
                  <div className={tableStyles.scrollbarContent} ref={contentRef} />
                </div>
              )}
            </DynamicSectionWrapper>
          ) : // Original logic for non-dynamic sections or sections with few rows
          draggable ? (
            <div
              style={{
                width: "100%",
                overflowX: !collapseable || !collapsed ? "scroll" : "visible",
                paddingBottom: "2px",
              }}
              ref={sectionRef}
              className={overlayControlledClassName}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <StyledTable
                  layout={fixedLayout ? "fixed" : undefined}
                  style={{
                    marginTop: "0px",
                    borderWidth: noBorders ? "0px" : "1px",
                  }}
                  className={className}>
                  <PortraitSectionTitle
                    sectionIndex={sectionIndex}
                    title={title}
                    center={center}
                    subtitle={subtitle}
                    showVitals={showVitals}
                    collapseable={collapseable}
                    collapsed={collapsed}
                  />
                  {(!collapseable || !collapsed || !title?.trim()) &&
                    generatedHeaders}
                  {(!collapseable || !collapsed) && (
                    <Droppable droppableId={droppableId} direction="vertical">
                      {provided => (
                        <tbody
                          {...provided.droppableProps}
                          ref={provided.innerRef}>
                          <Row
                            isOverlay={isOverlay}
                            showAddCommentButton={showAddCommentButton}
                            authRequiredForDeletion={authRequiredForDeletion}
                            rows={mappedState}
                            headers={headers}
                            showSubRowHeaders={showSubRowHeaders}
                            isDynamic={Boolean(dynamicRows || cloneRows)}
                            sectionIndex={sectionIndex}
                            strikethroughRows={strikethroughRows}
                            NMEAButton={NMEA_button}
                            draggable={true}
                            isShared={isShared}
                            sharedIndex={sharedIndex}
                          />
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  )}
                </StyledTable>
              </DragDropContext>
            </div>
          ) : (
            <StyledTableWrapper
              ref={sectionRef}
              className={overlayControlledClassName}
              isDynamic={Boolean(dynamicRows || cloneRows)}>
              <StyledTable
                w={fixedWidth ?? undefined}
                layout={fixedLayout ? "fixed" : undefined}
                className={className}
                style={{
                  marginTop: "0px",
                  borderWidth: noBorders ? "0px" : "1px",
                }}>
                <PortraitSectionTitle
                  sectionIndex={sectionIndex}
                  title={title}
                  center={center}
                  subtitle={subtitle}
                  showVitals={showVitals}
                  collapseable={collapseable}
                  collapsed={collapsed}
                />
                {(!collapseable || !collapsed || !title?.trim()) &&
                  generatedHeaders}
                {(!collapseable || !collapsed) && (
                  <Row
                    isOverlay={isOverlay}
                    showAddCommentButton={showAddCommentButton}
                    headers={headers}
                    showSubRowHeaders={showSubRowHeaders}
                    authRequiredForDeletion={authRequiredForDeletion}
                    rows={memoizedRows}
                    isDynamic={Boolean(dynamicRows || cloneRows)}
                    sectionIndex={sectionIndex}
                    strikethroughRows={strikethroughRows}
                    isShared={isShared}
                    sharedIndex={sharedIndex}
                  />
                )}
              </StyledTable>
            </StyledTableWrapper>
          )}

          {(dynamicRows || cloneRows) &&
            !isOverlay &&
            (!collapseable || !collapsed) &&
            !addRowsToTop && (
              <AddDynamicRow
                sectionsClassName={className}
                inlineSaving={inlineSaving}
                sectionIndex={sectionIndex}
                addRows={dynamicRows}
                cloneRows={cloneRows}
                copyPrevVal={copyPrevVal}
              />
            )}
          {(dynamicSection || cloneSection) &&
            !isOverlay &&
            (!collapseable || !collapsed) && (
              <ControlDynamicSection
                addSections={dynamicSection}
                cloneSections={cloneSection}
                sectionIndex={sectionIndex}
              />
            )}
        </div>
      </TimeoutProvider>
    );
  },
);

export default PortraitSection;
